import React from 'react'
import cn from 'classnames'
import { observer } from 'mobx-react'
import {
  GridApi,
  GridReadyEvent,
  BodyScrollEvent,
  SelectionChangedEvent,
  RowDoubleClickedEvent,
} from 'ag-grid-community'
import { AgGridReact, AgGridColumn } from 'ag-grid-react'

import ScriptStore from 'stores/script/ScriptStore'
import Script from 'stores/script/Script'
import RecordStore from 'stores/record/RecordStore'
import Record from 'stores/record/Record'
import TabStore from 'stores/tab/TabStore'

import createError from 'utils/createError'
import getRecordId from 'utils/getRecordId'

import RelationPill from './RelationPill'
import { TABLE_ROW_HEIGHT } from './Table'
import TableCellHeaderWithSearch from './TableCellHeaderWithSearch'
import TableCellRenderer from './TableCellRenderer'
import { IProps as ITableCellEditorParams } from './TableCellEditor'
import TableEmptyOverlay from './TableEmptyOverlay'

import styles from './relationInput.scss'

interface IProps extends ITableCellEditorParams {
  initialValue: object | null
}

interface IState {
  value: any // This is the connected record value, so it cannot be typed
}

class RelationInput extends React.Component<IProps, IState> {
  private container: React.RefObject<HTMLDivElement> = React.createRef()
  private table: React.RefObject<HTMLDivElement> = React.createRef()

  private loadedScript: Script
  private gridApi!: GridApi // ! because it will be assigned in handleGridReady

  constructor(props: IProps) {
    super(props)

    if (!props.field.typeAsModel) {
      throw createError(
        'RelationInput.constructor',
        'Invalid field.typeAsModel',
        {
          field: props.field.serialize(),
        }
      )
    }

    this.state = {
      value: props.initialValue ?? null,
    }

    this.loadedScript = ScriptStore.add(
      {
        name: null,
        frozen: true,
        modelId: props.field.typeAsModel.id,
        fieldIds: props.field.typeAsModel.fieldIds,
      },
      { skipPersist: true }
    )
    // Add (invalid) where filters for every field. The table's header will fill them out
    this.loadedScript.fields.forEach(f => {
      f.isScalar &&
        this.loadedScript.where.add({
          fieldIds: [f.id],
          operation:
            f.isInt || f.isFloat || f.isDecimal ? 'equals' : 'contains',
          value: null,
        })
    })
  }

  componentWillUnmount() {
    this.props.api?.resetRowHeights() // Set the row height back to normal
    this.props.api?.onRowHeightChanged()
  }

  // Called by parent ag-grid to get the final value when editing is finished
  getValue = () => this.state.value

  focus = () => {}

  // ag-grid takes over rendering, so it provides this in place of componentDidMount
  afterGuiAttached = async () => {
    if (!this.container.current || !this.table.current) {
      return
    }

    const { api, node, getTableDimensions } = this.props

    if (!api) {
      return
    }

    const parentTableDimensions = getTableDimensions()
    const ownDimensions = this.container.current.getBoundingClientRect()

    const tableHeight = node.rowHeight - TABLE_ROW_HEIGHT

    this.table.current.style.height = `${tableHeight}px` // And then set the accordion's height
    this.table.current.style.width = `${
      parentTableDimensions.width - 32 - 20
    }px` // 32 is the width of the checkbox, and 20 is for the 10px margin around

    this.table.current.style.left = `${
      parentTableDimensions.left - ownDimensions.left + 32 + 10 // 32 is width of the checkbox row, 10 is the width of the margin
    }px`
  }

  handleGridReady = async (e: GridReadyEvent) => {
    const { value } = this.state
    const { field } = this.props

    this.gridApi = e.api

    await this.loadedScript.run()

    // Pull up already connected record to the top
    const connectedRecordId = value
      ? getRecordId(field.typeAsModel!.id, value)
      : null
    const connectedRecord = RecordStore.get(connectedRecordId)
    this.gridApi.setRowData([
      ...(connectedRecord ? [connectedRecord] : []),
      ...this.loadedScript.records.filter(r => r.id !== connectedRecordId), // Remove the connected record to avoid duplication
    ])
    this.selectConnectedRecords()
  }

  handleScroll = async (e: BodyScrollEvent) => {
    const { node } = this.props
    this.gridApi.stopEditing()

    if (e.direction === 'horizontal') {
      return
    }

    if (
      this.loadedScript.recordIds.length >= (this.loadedScript.model.count || 0)
    ) {
      // We're already at the end of the list, no need to try and fetch more
      return
    }

    const tableHeight = node.rowHeight - TABLE_ROW_HEIGHT
    const rowCount = this.loadedScript.pagination.take

    // In the following line, we're trying to fetch the next page of records when you're 20 records from the bottom of the list
    if (
      !this.loadedScript.running &&
      e.top + tableHeight >= TABLE_ROW_HEIGHT * (rowCount - 20)
    ) {
      this.gridApi.applyTransaction({
        add: await this.loadedScript.loadMore(),
      })
      this.selectConnectedRecords()
    }
  }

  selectConnectedRecords = async () => {
    // Select already connected records
    const { value } = this.state
    const { field } = this.props

    if (value === null) {
      return
    }

    const connectedRecordId = getRecordId(field.typeAsModel!.id, value)
    this.gridApi.getRowNode(connectedRecordId)?.setSelected(true, true, true)
  }

  handleSelectionChanged = (e: SelectionChangedEvent) => {
    // Called *only* when a user changes selection. This feels like a quirk that may change.
    // Be sure to add thorough tests for this.

    const { value } = this.state
    const { field } = this.props

    const oldRecordId = value && getRecordId(field.typeAsModel!.id, value) // If old value was null, oldRecordId will bed `undefined`
    const newRecordId = e.api.getSelectedNodes().map(n => n.id)[0] || null

    if (!newRecordId) {
      // Nothing is selected anymore
      this.setState({
        value: null,
      })
      return
    }

    if (oldRecordId === newRecordId) {
      // Nothing changed
      return
    }

    // Even if `oldRecordId` is null / undefined, control flow will end up here
    this.gridApi.getRowNode(newRecordId)?.setSelected(true, true)

    this.setState({
      value: RecordStore.get(newRecordId)!.value, // ! is okay because this record will exist (since they'll be created in runQuery during this table's load)
    })
  }

  handleSearch = async () => {
    const { value } = this.state
    const { field } = this.props

    await this.loadedScript.run()

    const isSearching = !!Object.values(this.loadedScript.where.values).find(
      w => w.value !== null && w.value !== ''
    )

    if (isSearching) {
      this.gridApi.setRowData(this.loadedScript.records)
    } else {
      const connectedRecordId = value
        ? getRecordId(field.typeAsModel!.id, value)
        : null
      this.gridApi.setRowData([
        ...(value ? [RecordStore.get(connectedRecordId)] : []),
        ...this.loadedScript.records.filter(r => r.id !== connectedRecordId), // Remove the connected record to avoid duplication
      ])
    }

    this.selectConnectedRecords()
  }

  handleClickRelation = () => {
    this.props.api?.stopEditing()
  }

  handleRowDoubleClicked = (e: RowDoubleClickedEvent) => {
    this.handleSelectionChanged(e)
    this.props.stopEditing()
  }

  handleViewConnections = () => {
    const { field } = this.props
    const { value } = this.state

    if (value === null) {
      return
    }

    if (!field.typeAsModel) {
      // Should never happen
      return
    }

    const model = field.typeAsModel
    const connectedRecordId = value
      ? getRecordId(field.typeAsModel!.id, value)
      : null
    const record = RecordStore.get(connectedRecordId)

    if (!record) {
      return
    }

    const tab = TabStore.add({
      modelId: model.id,
      preview: false,
    })
    // Add a `where` filter to uniquely identify this ONE record
    model.uniqueIdentifiers.map(f => {
      tab.session!.script!.where.add({
        fieldIds: [f.id],
        operation: 'equals',
        value: record.value[f.name],
      }) // `!` is fine here because we know it is a script tab
    })
    TabStore.switch({ id: tab.id })
  }

  render() {
    const { value } = this.state
    const { field } = this.props
    const columnDefinitions = field.typeAsModel!.fields

    return (
      <>
        <div ref={this.container} className={styles.container}>
          <RelationPill
            type={field.type}
            isNull={!value}
            isPressed
            onClick={this.handleClickRelation}
          />
        </div>

        <div
          data-cy="input--relation"
          ref={this.table}
          className={cn(styles.table, 'ag-theme-relations')}
        >
          <AgGridReact
            rowSelection="single"
            getRowNodeId={params => (params as Record).id}
            suppressCellSelection
            headerHeight={64}
            frameworkComponents={{
              TableCellRenderer,
              TableCellHeaderWithSearch,
              TableEmptyOverlay,
            }}
            onGridReady={this.handleGridReady}
            onBodyScroll={this.handleScroll}
            onSelectionChanged={this.handleSelectionChanged}
            noRowsOverlayComponent="TableEmptyOverlay"
            // We're not using the infinite model here because there are a lot of things we want to do, and it'll just get in the way.
            // (Details in the constructor)
          >
            <AgGridColumn
              colId="checkbox"
              headerName=""
              editable={false}
              resizable={false}
              sortable={false}
              pinned={true}
              suppressNavigable={true}
              suppressMovable={true}
              maxWidth={32}
              checkboxSelection={true}
            />
            {columnDefinitions.map(c => (
              <AgGridColumn
                key={c.id}
                editable={false}
                resizable={false}
                sortable={false}
                suppressMovable={true}
                headerComponent="TableCellHeaderWithSearch"
                headerComponentParams={{
                  field: c,
                  script: this.loadedScript,
                  onSearch: this.handleSearch,
                }}
                cellRenderer="TableCellRenderer"
                cellRendererParams={{
                  field: c,
                }}
              />
            ))}
          </AgGridReact>

          <div className={styles.footer}>
            <button
              data-cy="open-in-new-tab"
              className={cn(styles.footerBtn, {
                [styles.disabled]: value === null,
              })}
              onClick={this.handleViewConnections}
            >
              Open in new tab
            </button>
          </div>
        </div>
      </>
    )
  }
}

export default observer(RelationInput)
